/**
 * Ojetá vozidla API — typed wrappers around `/v1/ojeta-vozidla/*`.
 *
 * Mirrors the OpenAPI shapes (`OjetaVozidlaListResponse`,
 * `OjetaVozidloSummaryResponse`, `OjetaVozidloDetailResponse`,
 * `OjetaNabidkyListResponse`, `OjetaNabidkaDetailResponse`,
 * `OjetaNabidkaSummaryResponse`). Re-declared locally so the module compiles
 * when the gitignored `frontend/src/types/api.ts` hasn't been refreshed.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { pujcovnaKeys } from '@/modules/pujcovna/api';
import { getAuthHeaders } from '@/lib/auth';

// ─── List ───────────────────────────────────────────────────────────────────

export interface OjetoVozidlo {
  id_vozidla: number;
  id_firmy: number;
  /** Name of the holding firma (list responses). */
  firma_nazev?: string | null;
  spz_vozu: string;
  znacka_vozu: number;
  vozidlo_model: number;
  provedeni: string;
  verze: string;
  motor: string;
  barva: string;
  prevodovka: string;
  calouneni: string;
  vin: string;
  nakupni_cena: string;
  nakup_platce_dph: number;
  prodejni_cena: string;
  stav_vozidla: string;
  datum_predani: string | null;
  poznamka: string;
  rezervace: string;
  serviska: string | null;
  prvni_registrace: string | null;
  stk_platnost: string | null;
  stav_km: string;
  datum_km: string | null;
  pujcsiauto: number;
  web: number;
  id_zakaznika: number;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
  jmeno_zakaznika: string | null;
  /** Transfer between group firmas (migration 0048). */
  preveden_do_firmy?: number | null;
  preveden_at?: string | null;
  preveden_do_firma?: string | null;
  prevzato_z_firmy?: number | null;
  prevzato_z_firma?: string | null;
}

interface ListResponse {
  vozidla: OjetoVozidlo[];
  total: number;
  /** Whole-result breakdown by state (first page only); the crown shows it instead of counting loaded rows. */
  pocty?: { skladovy: number; zakaznicky: number; predane: number };
}

export interface ListFilters {
  search?: string;
  spz_vozu?: string;
  znacka_vozu?: string;
  vozidlo_model?: string;
  provedeni?: string;
  prevodovka?: string;
  vin?: string;
  stav_vozidla?: string;
  datum_predani?: string;
  limit?: string;
  offset?: string;
  /** `skupina`: every firma of the group the caller can read (plan §6.5). Default: the active firma. */
  rozsah?: 'aktivni' | 'skupina';
}

function buildQuery(filters: Record<string, string | undefined | null>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (typeof value === 'string' && value.trim()) params.set(key, value.trim());
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useOjetaListQuery(filters: ListFilters) {
  return useQuery({
    queryKey: ['ojeta', 'list', filters],
    queryFn: ({ signal }) =>
      api.get<ListResponse>(`/v1/ojeta-vozidla${buildQuery({ ...filters })}`, { signal }),
    placeholderData: (previous) => previous,
  });
}

export function useOjetaListInfiniteQuery(
  filters: Omit<ListFilters, 'limit' | 'offset'>,
  options?: { pageSize?: number },
) {
  const pageSize = options?.pageSize ?? 300;
  return useInfiniteQuery({
    queryKey: ['ojeta', 'list', 'infinite', filters, pageSize],
    queryFn: ({ pageParam, signal }) =>
      api.get<ListResponse>(
        `/v1/ojeta-vozidla${buildQuery({ ...filters, limit: String(pageSize), offset: String(pageParam) })}`,
        { signal },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.vozidla.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
  });
}

export interface Brand {
  id: number;
  nazev: string;
}
interface BrandsResponse {
  brands: Brand[];
}

export function useOjetaBrandsQuery() {
  return useQuery({
    queryKey: ['ojeta', 'brands'],
    queryFn: ({ signal }) => api.get<BrandsResponse>('/v1/ojeta-vozidla/brands', { signal }),
  });
}

export interface Model {
  id: number;
  nazev_modelu: string;
}
interface ModelsResponse {
  models: Model[];
}

export function useOjetaModelsQuery(brandId: number | null) {
  return useQuery({
    queryKey: ['ojeta', 'models', brandId],
    enabled: brandId != null && Number.isFinite(brandId),
    queryFn: ({ signal }) =>
      api.get<ModelsResponse>(`/v1/ojeta-vozidla/models/${brandId}`, { signal }),
  });
}

// ─── Modified (price-changed) vehicles ──────────────────────────────────────
//
// Vehicles whose price changed
// upstream and need acknowledgement. GET lists, DELETE resolves a record.

export interface OjetaModifiedVehicle {
  id_vozidla: number;
  id_firmy: number;
  spz_vozu: string;
  znacka_vozu: number;
  vozidlo_model: number;
  provedeni: string;
  motor: string;
  barva: string;
  vin: string;
  stav_vozidla: string;
  prodejni_cena: string;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
}

interface OjetaModifiedVehiclesResponse {
  vozidla: OjetaModifiedVehicle[];
}

export function useOjetaModifiedVehiclesQuery() {
  return useQuery({
    queryKey: ['ojeta', 'modified'],
    queryFn: ({ signal }) =>
      api.get<OjetaModifiedVehiclesResponse>('/v1/ojeta-vozidla/modified', { signal }),
  });
}

export function useResolveOjetaModifiedVehicleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ status: 'ok' }>(`/v1/ojeta-vozidla/modified/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ojeta', 'modified'] });
    },
  });
}

// ─── Reference data ─────────────────────────────────────────────────────────

export interface VatRate {
  id_dph: number;
  hodnota: number;
  nazev: string;
  nazev_zkratka: string;
}
export interface DeliveryPlace {
  id_mista: number;
  nazev: string;
  ulice: string;
  psc: string;
  mesto: string;
}
export interface FinancingType {
  id_financovani: number;
  nazev: string;
}

export function useVatRatesQuery() {
  return useQuery({
    queryKey: ['reference', 'vat-rates'],
    staleTime: 60 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.get<{ rates: VatRate[] }>('/v1/reference/vat-rates', { signal }),
  });
}

export function useDeliveryPlacesQuery() {
  return useQuery({
    queryKey: ['reference', 'delivery-places'],
    staleTime: 60 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.get<{ places: DeliveryPlace[] }>('/v1/reference/delivery-places', { signal }),
  });
}

export function useFinancingTypesQuery() {
  return useQuery({
    queryKey: ['reference', 'financing-types'],
    staleTime: 60 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.get<{ types: FinancingType[] }>('/v1/reference/financing-types', { signal }),
  });
}

// ─── Detail / Summary ───────────────────────────────────────────────────────

export interface OjetoVozidloSummary extends OjetoVozidlo {
  prepared_sales_contract_id: number | null;
  closed_sales_contract_id: number | null;
  prepared_vykup_id: number | null;
  closed_vykup_id: number | null;
}

interface SummaryResponse {
  vozidlo: OjetoVozidloSummary;
}

export const ojetoSummaryKey = (id: number | null) => ['ojeta', 'summary', id] as const;

export function useOjetoSummaryQuery(id: number | null) {
  return useQuery({
    queryKey: ojetoSummaryKey(id),
    enabled: id != null && Number.isFinite(id),
    // Seeds the vehicle-card edit form — always refetch on mount so edit pages
    // open against fresh server state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<SummaryResponse>(`/v1/ojeta-vozidla/${id}/summary`, { signal }),
  });
}

// ─── Termíny (STK · servis) ─────────────────────────────────────────────────

export interface OjetoTermin {
  /** Which stock the car is in; absent = ojeté. Decides the detail link. */
  zdroj?: 'nove' | 'ojete';
  id_vozidla: number;
  spz_vozu: string;
  vin: string;
  stav_vozidla: string;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
  /** Firma holding the car; differs from the active one in a group view (`rozsah: 'skupina'`). */
  id_firmy?: number;
  firma_nazev?: string | null;
  druh: 'stk' | 'servis';
  /** YYYY-MM-DD */
  datum: string;
  /** Days until the deadline; negative when overdue. */
  dnu: number;
  /**
   * YYYY-MM-DD of the earliest servis internal event of a matching kind for
   * this SPZ (STK / Servisní prohlídka / both) from 30 days before the
   * deadline onwards, no upper limit; null = none.
   */
  naplanovano?: string | null;
}

export interface OjetaTerminyResponse {
  dnes: string;
  terminy: OjetoTermin[];
}

/** Stock vehicles whose STK / servisní prohlídka falls within the
 *  next `dny` days, plus everything already overdue. Company-wide (the API
 *  scopes by id_firmy); `rozsah: 'skupina'` spans the group firmas where the caller reads the module.
 *  Gate on an ojeta_vozidla read grant before enabling. */
export function useOjetaTerminyQuery(
  dny = 30,
  opts: { enabled?: boolean; rozsah?: 'aktivni' | 'skupina' } = {},
) {
  const rozsah = opts.rozsah ?? 'aktivni';
  return useQuery({
    queryKey: ['ojeta', 'terminy', dny, rozsah],
    enabled: opts.enabled ?? true,
    queryFn: ({ signal }) =>
      api.get<OjetaTerminyResponse>(`/v1/ojeta-vozidla/terminy?dny=${dny}&rozsah=${rozsah}`, { signal }),
  });
}

// ─── Vehicle write (Phase 11.6.7) ───────────────────────────────────────────
// Mirrors `OjetaVehicleBodySchema` in api/src/schemas/ojeta-vozidla.ts. All
// fields are optional — `POST /v1/ojeta-vozidla` and `PUT /v1/ojeta-vozidla/{id}`
// accept any subset. Brand + model are enforced server-side (must be > 0 on
// create).

export interface OjetoWriteBody {
  spz_vozu?: string;
  znacka_vozu?: number;
  vozidlo_model?: number;
  provedeni?: string;
  verze?: string;
  motor?: string;
  barva?: string;
  prevodovka?: string;
  calouneni?: string;
  vin?: string;
  nakupni_cena?: string;
  prodejni_cena?: string;
  nakup_platce_dph?: number;
  pujcsiauto?: number;
  web?: number;
  stav_vozidla?: string;
  stav_km?: string;
  datum_predani?: string | null;
  serviska?: string | null;
  prvni_registrace?: string | null;
  stk_platnost?: string | null;
  datum_km?: string | null;
  poznamka?: string;
  rezervace?: string;
  id_zakaznika?: number;
}

interface OjetoCreateResponse {
  id: number;
}
interface OjetoUpdateResponse {
  status: 'ok';
  requires_customer: boolean;
}

export function useCreateOjetoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OjetoWriteBody) =>
      api.post<OjetoCreateResponse>('/v1/ojeta-vozidla', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta'] }),
  });
}

export function useUpdateOjetoMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OjetoWriteBody) =>
      api.put<OjetoUpdateResponse>(`/v1/ojeta-vozidla/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ojeta', 'summary', id] });
      qc.invalidateQueries({ queryKey: ['ojeta'] });
      // A VIN edit moves the car's rental fleet entry with it (stock-exit hook).
      qc.invalidateQueries({ queryKey: pujcovnaKeys.vozidla });
    },
  });
}

/**
 * Assign a customer to a used-vehicle card. PUT /v1/ojeta-vozidla/{id}/assign-customer — the body
 * schema (`OjetaAssignVehicleCustomerBodySchema`) requires a positive int, so
 * there is no unassign variant.
 */
export function useAssignOjeteCustomerMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id_zakaznika: number) =>
      api.put<{ status: 'ok' }>(`/v1/ojeta-vozidla/${id}/assign-customer`, { id_zakaznika }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ojeta', 'summary', id] });
      qc.invalidateQueries({ queryKey: ['ojeta'] });
    },
  });
}

/**
 * Hand a used vehicle over to its customer. Sets `datum_predani` + flips `stav_vozidla` to
 * "Předaný zákazníkovi" server-side. `target` is fixed to 'customer' (the only
 * value the endpoint accepts today). Invalidates the summary so the detail
 * refreshes the stav badge + handover date in place.
 */
export function useOjetoHandoverMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (datum_predani: string) =>
      api.put<{ status: 'ok' }>(`/v1/ojeta-vozidla/${id}/handover`, {
        target: 'customer',
        datum_predani,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ojeta', 'summary', id] });
      qc.invalidateQueries({ queryKey: ['ojeta'] });
      // A handover takes the car out of the rental fleet (stock-exit hook).
      qc.invalidateQueries({ queryKey: pujcovnaKeys.vozidla });
    },
  });
}

// ─── Nabídky ────────────────────────────────────────────────────────────────

export interface NabidkaListItem {
  id_nabidky: number;
  znacka_vozu: number;
  vozidlo_model: number;
  cena_celkem: string | null;
  id_uzivatele: number;
  id_dph: number;
  datum_nabidky: string | null;
  id_zakaznika: number;
  id_vozidla: number | null;
  spz_vozu: string | null;
  jmeno: string | null;
  jmeno_uzivatele: string | null;
  text_interni_poznamka: string | null;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
}

interface NabidkyListResponse {
  nabidky: NabidkaListItem[];
  total: number;
}

export interface NabidkyListFilters {
  hledanejmeno?: string;
  cislo_nabidky?: string;
  limit?: string;
  offset?: string;
}

export function useNabidkyQuery(filters: NabidkyListFilters) {
  return useQuery({
    queryKey: ['ojeta', 'nabidky', filters],
    queryFn: ({ signal }) =>
      api.get<NabidkyListResponse>(
        `/v1/ojeta-vozidla/nabidky${buildQuery({ ...filters })}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

export function useNabidkyInfiniteQuery(
  filters: Omit<NabidkyListFilters, 'limit' | 'offset'>,
  options?: { pageSize?: number },
) {
  const pageSize = options?.pageSize ?? 300;
  return useInfiniteQuery({
    queryKey: ['ojeta', 'nabidky', 'infinite', filters, pageSize],
    queryFn: ({ pageParam, signal }) =>
      api.get<NabidkyListResponse>(
        `/v1/ojeta-vozidla/nabidky${buildQuery({ ...filters, limit: String(pageSize), offset: String(pageParam) })}`,
        { signal },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.nabidky.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
  });
}

export interface NabidkaDetail {
  id_nabidky: number;
  id_firmy: number;
  id_zakaznika: number;
  id_vozidla: number;
  id_uzivatele: number;
  spz_vozu: string;
  znacka_vozu: number;
  vozidlo_model: number;
  provedeni: string;
  verze: string;
  barva: string;
  motor: string;
  prevodovka: string;
  calouneni: string;
  zakladni_cena: string;
  sleva_zakladni_cena: string;
  cena_celkem: string | null;
  id_dph: number;
  datum_nabidky: string | null;
  datum_uzavreni: string;
  typ_financovani: number;
  datum_predani: string | null;
  misto_predani: number;
  stav_km: string;
  prvni_registrace: string | null;
  jmeno: string | null;
  jmeno_uzivatele: string | null;
  text_interni_poznamka: string | null;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
}

interface NabidkaDetailResponse {
  nabidka: NabidkaDetail;
}

export function useNabidkaDetailQuery(id: number | null) {
  return useQuery({
    queryKey: ['ojeta', 'nabidka', 'detail', id],
    enabled: id != null && Number.isFinite(id),
    // Backs the offer wizard prefills — always refetch on mount for fresh state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<NabidkaDetailResponse>(`/v1/ojeta-vozidla/nabidky/${id}`, { signal }),
  });
}

export interface NabidkaSummary extends NabidkaDetail {
  hodnota_dph: string | null;
  nazev_dph: string | null;
  jmeno_zakaznika: string | null;
  dodatek_obsah: string | null;
}

export interface NabidkaSummaryResponse {
  nabidka: NabidkaSummary;
  company: Record<string, unknown> | null;
  seller: { jmeno: string; prijmeni: string } | null;
  vatRates: { id_dph: number; hodnota: string; nazev_zkratka: string }[];
  deliveryPlace: Record<string, unknown> | null;
  financingType: Record<string, unknown> | null;
  attachments: { id_prilohy: number; nazev: string; poznamka: string; id_dokumentu: number; klic: string }[];
}

export function useNabidkaSummaryQuery(id: number | null) {
  return useQuery({
    queryKey: ['ojeta', 'nabidka', 'summary', id],
    enabled: id != null && Number.isFinite(id),
    // Offer detail surface — always refetch on mount for fresh data.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<NabidkaSummaryResponse>(`/v1/ojeta-vozidla/nabidky/${id}/summary`, { signal }),
  });
}

// ─── Nabídka mutations (Phase 7.3 wizard) ──────────────────────────────────

export function useCreateNabidkaMutation() {
  return useMutation({
    mutationFn: (id_zakaznika: number) =>
      api.post<{ id: number }>('/v1/ojeta-vozidla/nabidky', { id_zakaznika }),
  });
}

/**
 * Create an offer seeded from a used vehicle (carries SPZ, brand/model, motor,
 * registration, price basis, etc.), then optionally assign the picked customer.
 * Backs the "Vytvořit nabídku" action on the vehicle detail card — the blank
 * `useCreateNabidkaMutation` would drop the vehicle link entirely.
 */
export function useCreateNabidkaFromVehicleMutation() {
  return useMutation({
    mutationFn: async ({
      id_vozidla,
      id_zakaznika,
    }: {
      id_vozidla: number;
      id_zakaznika?: number;
    }) => {
      const res = await api.post<{ id: number }>(
        '/v1/ojeta-vozidla/nabidky/create-from-vehicle',
        { id_vozidla },
      );
      if (id_zakaznika && id_zakaznika > 0) {
        await api.post<{ status: 'ok' }>(
          `/v1/ojeta-vozidla/nabidky/${res.id}/customer`,
          { id_zakaznika },
        );
      }
      return res;
    },
  });
}

export function useAssignNabidkaCustomerMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id_zakaznika: number) =>
      api.post<{ status: 'ok' }>(`/v1/ojeta-vozidla/nabidky/${id}/customer`, { id_zakaznika }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'nabidka'] }),
  });
}

export interface NabidkaUpdateBody {
  spz_vozu?: string;
  znacka_vozu?: number | null;
  vozidlo_model?: number | null;
  provedeni?: string;
  verze?: string;
  barva?: string;
  motor?: string;
  prevodovka?: string;
  calouneni?: string;
  zakladni_cena?: number | null;
  stav_km?: string;
  prvni_registrace?: string | null;
  id_dph?: number | null;
  typ_financovani?: number | null;
  datum_predani?: string;
  misto_predani?: number | null;
}

export function useUpdateNabidkaMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NabidkaUpdateBody) =>
      api.put<{ status: 'ok' }>(`/v1/ojeta-vozidla/nabidky/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'nabidka'] }),
  });
}

/**
 * Inline-saves `text_interni_poznamka` via the dedicated
 * `POST /v1/ojeta-vozidla/nabidky/{id}/note` endpoint (mirrors the
 * vozidla nabídka's PUT body — both modules upsert the
 * `ojeta_vozidla_nabidka_poznamka` / `vozidla_nabidka_poznamka` row).
 * Used by the InlineNote primitive on the Phase 12.7.4 NabidkaDetail.
 */
export function useSaveNabidkaNoteMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text_interni_poznamka: string) =>
      api.post<{ status: 'ok' }>(`/v1/ojeta-vozidla/nabidky/${id}/note`, {
        text_interni_poznamka,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'nabidka'] }),
  });
}

export function useSetNabidkaDiscountMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { sleva_procenta?: number | null; sleva_bez_dph?: number | null }) =>
      api.post<{ status: 'ok' }>(`/v1/ojeta-vozidla/nabidky/${id}/discount`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'nabidka'] }),
  });
}

export function useConvertNabidkaMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ id_smlouvy: number }>(`/v1/ojeta-vozidla/nabidky/${id}/convert-to-smlouva`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta'] }),
  });
}

/**
 * Duplicate / cross-company copy of an offer. The server deep-copies the nabídka and returns the
 * new id; omitting `id_cilove_firmy` (or sending the current company)
 * duplicates in place, another company copies across dealerships.
 */
export function useCopyNabidkaMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, id_cilove_firmy }: { id: number; id_cilove_firmy?: number }) =>
      api.post<{ id: number }>(
        `/v1/ojeta-vozidla/nabidky/${id}/copy`,
        id_cilove_firmy ? { id_cilove_firmy } : {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'nabidka'] }),
  });
}

/**
 * Upserts the customer-visible addendum (`dodatek_obsah`, "Dodatečné
 * informace" → POST /dodatek). Invalidate the summary so the
 * detail re-renders the fresh text.
 */
export function useSaveNabidkaDodatekMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (obsah: string) =>
      api.post<{ status: 'ok' }>(`/v1/ojeta-vozidla/nabidky/${id}/dodatek`, { obsah }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'nabidka'] }),
  });
}

// ─── Smlouvy (Phase 7.4 — list + wizard + protokol) ─────────────────────────

export interface SmlouvaListItem {
  id_smlouvy: number;
  stav_smlouvy: string;
  spz_vozu: string;
  znacka_vozu: number;
  cena_celkem: string;
  id_uzivatele: number;
  id_dph: number;
  id_vozidla: number;
  id_zakaznika: number | null;
  datum_uzavreni: string;
  jmeno: string | null;
  jmeno_uzivatele: string | null;
  text_interni_poznamka: string | null;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
}

interface SmluvyListResponse {
  smlouvy: SmlouvaListItem[];
  total: number;
}

export interface SmluvyListFilters {
  hledanejmeno?: string;
  cislo_smlouvy?: string;
  limit?: string;
  offset?: string;
}

export function useOjetaSmluvyQuery(filters: SmluvyListFilters) {
  return useQuery({
    queryKey: ['ojeta', 'smlouvy', 'list', filters],
    queryFn: ({ signal }) =>
      api.get<SmluvyListResponse>(
        `/v1/ojeta-vozidla/smlouvy${buildQuery({ ...filters })}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

export function useOjetaSmluvyInfiniteQuery(
  filters: Omit<SmluvyListFilters, 'limit' | 'offset'>,
  options?: { pageSize?: number },
) {
  const pageSize = options?.pageSize ?? 300;
  return useInfiniteQuery({
    queryKey: ['ojeta', 'smlouvy', 'list', 'infinite', filters, pageSize],
    queryFn: ({ pageParam, signal }) =>
      api.get<SmluvyListResponse>(
        `/v1/ojeta-vozidla/smlouvy${buildQuery({ ...filters, limit: String(pageSize), offset: String(pageParam) })}`,
        { signal },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.smlouvy.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
  });
}

export interface SmlouvaDetail {
  id_smlouvy: number;
  id_firmy: number;
  id_zakaznika: number;
  id_vozidla: number;
  id_uzivatele: number;
  spz_vozu: string;
  znacka_vozu: number;
  vozidlo_model: number;
  provedeni: string;
  verze: string;
  barva: string;
  motor: string;
  prevodovka: string;
  calouneni: string;
  zakladni_cena: string;
  sleva_zakladni_cena: string;
  cena_celkem: string;
  id_dph: number;
  stav_smlouvy: string;
  datum_uzavreni: string;
  typ_financovani: number;
  datum_predani: string;
  misto_predani: number;
  stav_km: string;
  prvni_registrace: string | null;
  predavaci_protokol: string;
  jmeno: string | null;
  jmeno_uzivatele: string | null;
  text_interni_poznamka: string | null;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
}

interface SmlouvaDetailResponse {
  smlouva: SmlouvaDetail;
}

export function useSmlouvaDetailQuery(id: number | null) {
  return useQuery({
    queryKey: ['ojeta', 'smlouva', 'detail', id],
    enabled: id != null && Number.isFinite(id),
    // Backs the contract wizard prefills — always refetch on mount for fresh state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<SmlouvaDetailResponse>(`/v1/ojeta-vozidla/smlouvy/${id}`, { signal }),
  });
}

export interface SmlouvaSummary extends SmlouvaDetail {
  hodnota_dph: string | null;
  nazev_dph: string | null;
  jmeno_zakaznika: string | null;
}

export interface SmlouvaSummaryResponse {
  smlouva: SmlouvaSummary;
  company: Record<string, unknown> | null;
  seller: { jmeno: string; prijmeni: string } | null;
  vatRates: { id_dph: number; hodnota: string; nazev_zkratka: string }[];
  deliveryPlace: Record<string, unknown> | null;
  financingType: Record<string, unknown> | null;
  attachments: { id_prilohy: number; nazev: string; poznamka: string; id_dokumentu: number; klic: string }[];
}

export function useSmlouvaSummaryQuery(id: number | null) {
  return useQuery({
    queryKey: ['ojeta', 'smlouva', 'summary', id],
    enabled: id != null && Number.isFinite(id),
    // Contract detail surface — always refetch on mount for fresh data.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<SmlouvaSummaryResponse>(`/v1/ojeta-vozidla/smlouvy/${id}/summary`, { signal }),
  });
}

export interface HandoverProtocol {
  id_protokolu: number;
  id_smlouvy: number;
  datum: string;
  technicky_prukaz: string;
  pocet_klicu: string;
  kod_barvy: string;
  vin: string;
  registracni_znacka: string;
  servis_mesice: string;
  servis_km: string;
  odhad_servisu: string;
}

interface HandoverProtocolResponse {
  found: boolean;
  protocol: HandoverProtocol | null;
}

export function useHandoverProtocolQuery(id: number | null) {
  return useQuery({
    queryKey: ['ojeta', 'smlouva', 'protocol', id],
    enabled: id != null && Number.isFinite(id),
    queryFn: ({ signal }) =>
      api.get<HandoverProtocolResponse>(
        `/v1/ojeta-vozidla/smlouvy/${id}/handover-protocol`,
        { signal },
      ),
  });
}

export function useCreateSmlouvaMutation() {
  return useMutation({
    mutationFn: (id_zakaznika: number) =>
      api.post<{ id: number }>('/v1/ojeta-vozidla/smlouvy', { id_zakaznika }),
  });
}

/**
 * Create a contract seeded from a vehicle card. The server copies the vehicle's
 * identity (SPZ, brand/model, motor, …) and its assigned customer (may be 0 —
 * the endpoint does not require one) into a fresh "Připravená" smlouva.
 */
export function useCreateSmlouvaFromVehicleMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id_vozidla: number) =>
      api.post<{ id: number }>('/v1/ojeta-vozidla/smlouvy/create-from-vehicle', {
        id_vozidla,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta'] }),
  });
}

export function useAssignSmlouvaCustomerMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id_zakaznika: number) =>
      api.put<{ status: 'ok' }>(`/v1/ojeta-vozidla/smlouvy/${id}/customer`, { id_zakaznika }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'smlouva'] }),
  });
}

export interface SmlouvaUpdateBody {
  spz_vozu?: string;
  znacka_vozu?: number | null;
  vozidlo_model?: number | null;
  provedeni?: string;
  verze?: string;
  barva?: string;
  motor?: string;
  prevodovka?: string;
  calouneni?: string;
  zakladni_cena?: number | null;
  stav_km?: string;
  prvni_registrace?: string | null;
  id_dph?: number | null;
  typ_financovani?: number | null;
  datum_predani?: string;
  misto_predani?: number | null;
}

export function useUpdateSmlouvaMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SmlouvaUpdateBody) =>
      api.put<{ status: 'ok' }>(`/v1/ojeta-vozidla/smlouvy/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'smlouva'] }),
  });
}

/**
 * Phase 12.7.5 — inline-saves `text_interni_poznamka` via the dedicated
 * `PUT /v1/ojeta-vozidla/smlouvy/{id}/note` endpoint (the smlouva variant
 * is PUT, the nabidka variant is POST — symmetry diverges with the
 * underlying repo's `saveNote` upsert semantics).
 */
export function useSaveSmlouvaNoteMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text_interni_poznamka: string) =>
      api.put<{ status: 'ok' }>(`/v1/ojeta-vozidla/smlouvy/${id}/note`, {
        text_interni_poznamka,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'smlouva'] }),
  });
}

export function useSetSmlouvaDiscountMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { sleva_procenta?: number | null; sleva_bez_dph?: number | null }) =>
      api.put<{ status: 'ok' }>(`/v1/ojeta-vozidla/smlouvy/${id}/discount`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'smlouva'] }),
  });
}

export interface CloseSmlouvaResult {
  status: 'ok' | 'reservation';
  message: string;
  vehicleId?: number;
}

export interface CloseSmlouvaBody {
  id_vozidla?: number;
  datum_uzavreni?: string;
  rezervace_potvrzena?: 'Ano';
}

export function useCloseSmlouvaMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CloseSmlouvaBody) =>
      api.post<CloseSmlouvaResult>(`/v1/ojeta-vozidla/smlouvy/${id}/close`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta'] }),
  });
}

export interface HandoverProtocolBody {
  datum?: string;
  technicky_prukaz?: string;
  pocet_klicu?: string;
  kod_barvy?: string;
  vin?: string;
  registracni_znacka?: string;
  servis_mesice?: string;
  servis_km?: string;
  odhad_servisu?: string;
}

export function useCreateHandoverProtocolMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: HandoverProtocolBody) =>
      api.post<{ status: 'ok' }>(
        `/v1/ojeta-vozidla/smlouvy/${id}/handover-protocol`,
        body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'smlouva'] }),
  });
}

export function useUpdateHandoverProtocolMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: HandoverProtocolBody) =>
      api.put<{ status: 'ok' }>(
        `/v1/ojeta-vozidla/smlouvy/${id}/handover-protocol`,
        body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'smlouva'] }),
  });
}

// ─── Výkup (Phase 7.5 — VIN lookup → wizard) ────────────────────────────────

export interface VykupListItem {
  id_smlouvy: number;
  stav_smlouvy: string;
  spz_vozu: string;
  cena_celkem: string;
  id_uzivatele: number;
  id_zakaznika: number;
  jmeno: string | null;
  jmeno_uzivatele: string | null;
  text_interni_poznamka: string | null;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
}

interface VykupListResponse {
  smlouvy: VykupListItem[];
  total: number;
}

export interface VykupListFilters {
  hledanejmeno?: string;
  cislo_smlouvy?: string;
  limit?: string;
  offset?: string;
}

export function useVykupListQuery(filters: VykupListFilters) {
  return useQuery({
    queryKey: ['ojeta', 'vykup', 'list', filters],
    queryFn: ({ signal }) =>
      api.get<VykupListResponse>(`/v1/ojeta-vozidla/vykup${buildQuery({ ...filters })}`, {
        signal,
      }),
    placeholderData: (previous) => previous,
  });
}

export function useVykupListInfiniteQuery(
  filters: Omit<VykupListFilters, 'limit' | 'offset'>,
  options?: { pageSize?: number },
) {
  const pageSize = options?.pageSize ?? 300;
  return useInfiniteQuery({
    queryKey: ['ojeta', 'vykup', 'list', 'infinite', filters, pageSize],
    queryFn: ({ pageParam, signal }) =>
      api.get<VykupListResponse>(
        `/v1/ojeta-vozidla/vykup${buildQuery({ ...filters, limit: String(pageSize), offset: String(pageParam) })}`,
        { signal },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.smlouvy.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
  });
}

export interface VykupDetail {
  id_smlouvy: number;
  id_firmy: number;
  id_zakaznika: number;
  id_vozidla: number;
  id_uzivatele: number;
  spz_vozu: string;
  vin: string;
  znacka_vozu: number;
  vozidlo_model: number;
  provedeni: string;
  verze: string;
  barva: string;
  motor: string;
  prevodovka: string;
  calouneni: string;
  zakladni_cena: string;
  sleva_zakladni_cena: string;
  cena_celkem: string;
  id_dph: number;
  stav_smlouvy: string;
  datum_uzavreni: string;
  datum_predani: string;
  misto_predani: number;
  specifikace_protiuctu: string;
  stav_km: string;
  stk_platnost: string | null;
  prvni_registrace: string | null;
  jmeno: string | null;
  jmeno_uzivatele: string | null;
  text_interni_poznamka: string | null;
  nazev_znacky: string | null;
  nazev_modelu: string | null;
}

interface VykupDetailResponse {
  smlouva: VykupDetail;
}

export function useVykupDetailQuery(id: number | null) {
  return useQuery({
    queryKey: ['ojeta', 'vykup', 'detail', id],
    enabled: id != null && Number.isFinite(id),
    // Backs the výkup wizard prefills — always refetch on mount for fresh state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<VykupDetailResponse>(`/v1/ojeta-vozidla/vykup/${id}`, { signal }),
  });
}

export interface VykupSummary extends VykupDetail {
  hodnota_dph: string | null;
  nazev_dph: string | null;
  jmeno_zakaznika: string | null;
}

export interface VykupSummaryResponse {
  smlouva: VykupSummary;
  company: Record<string, unknown> | null;
  seller: { jmeno: string; prijmeni: string } | null;
  vatRates: { id_dph: number; hodnota: string; nazev_zkratka: string }[];
  deliveryPlace: Record<string, unknown> | null;
  attachments: { id_prilohy: number; nazev: string; poznamka: string; id_dokumentu: number; klic: string }[];
}

export function useVykupSummaryQuery(id: number | null) {
  return useQuery({
    queryKey: ['ojeta', 'vykup', 'summary', id],
    enabled: id != null && Number.isFinite(id),
    // Výkup detail surface — always refetch on mount for fresh data.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<VykupSummaryResponse>(`/v1/ojeta-vozidla/vykup/${id}/summary`, { signal }),
  });
}

export interface VinLookupResult {
  stav: string;
  zdroj?: 'Ojeté vozy' | 'Nová vozidla';
  id_vozidla?: number;
  spz_vozu?: string;
  znacka_vozu?: number;
  vozidlo_model?: number;
  nazev_znacky?: string;
  nazev_modelu?: string;
  provedeni?: string;
  verze?: string;
  barva?: string;
  motor?: string;
  prevodovka?: string;
  calouneni?: string;
  stav_km?: string;
  prvni_registrace?: string;
  stk_platnost?: string;
  id_zakaznika?: number;
}

export function useVinLookupMutation() {
  return useMutation({
    mutationFn: (vin: string) =>
      api.get<VinLookupResult>(
        `/v1/ojeta-vozidla/vykup/vin-lookup?vin=${encodeURIComponent(vin)}`,
      ),
  });
}

export interface CreateVykupBody {
  zdroj_vozidla?: string;
  id_vozidla?: number;
  id_zakaznika?: number;
  vin?: string;
  spz_vozu?: string;
  znacka_vozu?: number;
  vozidlo_model?: number;
  provedeni?: string;
  verze?: string;
  barva?: string;
  motor?: string;
  prevodovka?: string;
  calouneni?: string;
  zakladni_cena?: number;
  sleva_zakladni_cena?: number;
  id_dph?: number;
  stav_km?: string;
  prvni_registrace?: string;
  stk_platnost?: string;
}

export function useCreateVykupMutation() {
  return useMutation({
    mutationFn: (body: CreateVykupBody) =>
      api.post<{ id_smlouvy: number; id_vozidla: number }>(
        '/v1/ojeta-vozidla/vykup',
        body,
      ),
  });
}

export interface UpdateVykupBody {
  spz_vozu?: string;
  vin?: string;
  znacka_vozu?: number;
  vozidlo_model?: number;
  provedeni?: string;
  verze?: string;
  barva?: string;
  motor?: string;
  prevodovka?: string;
  calouneni?: string;
  zakladni_cena?: number;
  sleva_zakladni_cena?: number;
  id_dph?: number;
  stav_km?: string;
  prvni_registrace?: string | null;
  stk_platnost?: string | null;
  datum_predani?: string;
  misto_predani?: number;
  specifikace_protiuctu?: string;
}

export function useUpdateVykupMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateVykupBody) =>
      api.put<{ status: 'ok' }>(`/v1/ojeta-vozidla/vykup/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'vykup'] }),
  });
}

export function useAssignVykupCustomerMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id_zakaznika: number) =>
      api.post<{ status: 'ok' }>(`/v1/ojeta-vozidla/vykup/${id}/customer`, { id_zakaznika }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'vykup'] }),
  });
}

export function useCloseVykupMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { datum_uzavreni?: string }) =>
      api.post<{ status: 'ok' }>(`/v1/ojeta-vozidla/vykup/${id}/close`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta'] }),
  });
}

/**
 * Phase 12.7.6 — inline-save `text_interni_poznamka` via
 * `POST /v1/ojeta-vozidla/vykup/{id}/note`.
 */
export function useSaveVykupNoteMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text_interni_poznamka: string) =>
      api.post<{ status: 'ok' }>(`/v1/ojeta-vozidla/vykup/${id}/note`, {
        text_interni_poznamka,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ojeta', 'vykup'] }),
  });
}

// ─── Soubory / S3 (Phase 12.7.4 — ojeta detail pages) ──────────────────────
//
// Mirrors the vozidla AttachmentParent + hooks against the ojeta-vozidla
// /files endpoints (each entity — nabidky / smlouvy / vykup — has its
// own GET / POST / PUT-by-id / DELETE-by-id triple).

export interface OjetaAttachment {
  id_prilohy: number;
  nazev: string;
  poznamka: string | null;
  id_dokumentu: number;
  klic: string;
}

type OjetaAttachmentParent = 'nabidky' | 'smlouvy' | 'vykup';

function ojetaAttachmentBase(parent: OjetaAttachmentParent, id: number) {
  return `/v1/ojeta-vozidla/${parent}/${id}/files`;
}

export function useOjetaAttachmentsQuery(parent: OjetaAttachmentParent, id: number | null) {
  return useQuery({
    queryKey: ['ojeta', parent, 'files', id],
    enabled: id !== null,
    queryFn: async ({ signal }) => {
      // The list endpoints return `{ files }`; detail pages read
      // `data.attachments`. Normalize here (and tolerate either key) so the
      // attachment list renders instead of throwing on `attachments.length`.
      const res = await api.get<{ files?: OjetaAttachment[]; attachments?: OjetaAttachment[] }>(
        ojetaAttachmentBase(parent, id as number),
        { signal },
      );
      return { attachments: res.attachments ?? res.files ?? [] };
    },
  });
}

export function useUploadOjetaAttachmentMutation(parent: OjetaAttachmentParent, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; nazev?: string; poznamka?: string }) => {
      const fd = new FormData();
      fd.append('file', input.file);
      if (input.nazev) fd.append('nazev', input.nazev);
      if (input.poznamka) fd.append('poznamka', input.poznamka);
      const headers: Record<string, string> = {
        Accept: 'application/json',
        ...getAuthHeaders(),
      };
      const res = await fetch(`/api${ojetaAttachmentBase(parent, id)}`, {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        headers,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      return (await res.json()) as { id: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ojeta', parent, 'files', id] });
      qc.invalidateQueries({ queryKey: ['ojeta', parent, 'summary', id] });
    },
  });
}

export function useUpdateOjetaAttachmentMutation(parent: OjetaAttachmentParent, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { fileId: number; nazev: string; poznamka?: string }) =>
      api.put<{ status: 'ok' }>(`${ojetaAttachmentBase(parent, id)}/${input.fileId}`, {
        nazev: input.nazev,
        poznamka: input.poznamka ?? '',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ojeta', parent, 'files', id] });
      qc.invalidateQueries({ queryKey: ['ojeta', parent, 'summary', id] });
    },
  });
}

export function useDeleteOjetaAttachmentMutation(parent: OjetaAttachmentParent, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: number) =>
      api.delete<{ status: 'ok' }>(`${ojetaAttachmentBase(parent, id)}/${fileId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ojeta', parent, 'files', id] });
      qc.invalidateQueries({ queryKey: ['ojeta', parent, 'summary', id] });
    },
  });
}
